import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Header, Icon, Image, Segment } from 'semantic-ui-react';
import { Formik, Form, Field, FieldProps } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import MyInputText from '../../app/common/form/MyTextInput';
import MyTextArea from '../../app/common/form/MyTextArea';
import Select from 'react-select';
import { sportOptions } from '../../app/common/options/sportOptions';
import { TeamDto } from '../../app/models/Team';
import PhotoUploadWidget from '../../app/common/imageUpload/PhotoUploadWidget';
import { GameLocationPicker } from '../../components/map/GameLocationPicker';
import LoadingComponent from '../../app/layout/LoadingComponent';

interface Props {
  /** Optional: when used as a create form, no team is passed. */
  team?: TeamDto;
}

export default observer(function TeamForm({ team: teamProp }: Props) {
  const { t } = useTranslation();
  const { teamStore } = useStore();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();   // present on /teams/:id/edit
  const [showLogoUpload, setShowLogoUpload] = useState(false);

  // The team we actually use — prefer the resolved one from the store
  const [resolvedTeam, setResolvedTeam] = useState<TeamDto | undefined>(teamProp);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    // Edit mode: we need the team data regardless of where we came from.
    // Always fetch directly — no cache — so the form is always up-to-date.
    if (!id) return;                        // create mode, nothing to load

    let cancelled = false;

    const fetchTeam = async () => {
      setLoadError(false);
      try {
        // Bypass registry cache: call agent directly so we never show stale data
        // and never depend on selectedTeam being populated by a previous page.
        const { agent } = await import('../../app/api/agent');
        const fresh = await agent.Teams.getTeam(id);
        if (!cancelled) setResolvedTeam(fresh);
      } catch (err) {
        console.error('TeamForm: failed to load team', err);
        if (!cancelled) setLoadError(true);
      }
    };

    fetchTeam();

    return () => { cancelled = true; };
  }, [id]);

  // Show loader while waiting for team data in edit mode

  if (id && !resolvedTeam && !loadError) {
    return <LoadingComponent content={t('teams.loading')} />;
  }

  if (loadError) {
    return (
      <Segment>
        <Header as="h3" color="red">
          <Icon name="exclamation triangle" />
          {t('teams.errors.loadFailed')}
        </Header>
        <Button basic onClick={() => navigate(-1)}>{t('teams.form.cancel')}</Button>
      </Segment>
    );
  }

  const team = resolvedTeam;

  const initialValues = {
    name: team?.name ?? '',
    sportID: team?.sportID ?? '',
    description: team?.description ?? '',
    adminPhone: team?.adminPhone ?? '',
    latitude: team?.latitude ?? 0,
    longitude: team?.longitude ?? 0,
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(t('teams.validation.nameRequired')),
    sportID: Yup.string().required(t('teams.validation.sportRequired')),
    adminPhone: Yup.string()
      .required(t('teams.validation.adminPhoneRequired'))
      .matches(/^\+?[0-9\s\-().]{7,20}$/, t('teams.validation.adminPhoneInvalid')),
  });

  const localizedSports = sportOptions.map(o => ({
    ...o,
    label: t(`sports.${o.label.toLowerCase()}`) || o.label,
  }));

  async function handleSubmit(values: typeof initialValues) {
    if (team) {
      const ok = await teamStore.updateTeam(team.id, {
        name: values.name,
        description: values.description,
        adminPhone: values.adminPhone,
        latitude: values.latitude,
        longitude: values.longitude,
      });
      if (ok) {
        teamStore.loadingInitial = true;
        navigate(`/teams/${team.id}`);
      }
    } else {
      const newId = await teamStore.createTeam({
        name: values.name,
        sportID: values.sportID,
        description: values.description,
        adminPhone: values.adminPhone,
        latitude: values.latitude || undefined,
        longitude: values.longitude || undefined,
      });
      if (newId) navigate(`/teams/${newId}`);
    }
  }

  return (
    <Segment clearing style={{ maxWidth: 700, margin: '0 auto' }}>
      <Header
        content={team ? t('teams.form.editTitle') : t('teams.form.createTitle')}
        sub
        color="teal"
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ setFieldValue, values, isValid, isSubmitting }) => (
          <Form className="ui form error">

            {/* Name */}
            <MyInputText
              name="name"
              placeholder={t('teams.form.namePlaceholder')}
              label={t('teams.form.nameLabel')}
            />

            {/* Sport — disabled when editing */}
            <div style={{ marginBottom: 15 }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
                {t('teams.form.sportLabel')} *
              </label>
              <Select
                options={localizedSports}
                value={localizedSports.find(o => o.value === values.sportID) || null}
                onChange={opt => setFieldValue('sportID', opt?.value || '')}
                placeholder={t('teams.form.sportPlaceholder')}
                isSearchable
                isDisabled={!!team}
              />
            </div>

            {/* Description */}
            <MyTextArea
              rows={3}
              name="description"
              placeholder={t('teams.form.descriptionPlaceholder')}
              label={t('teams.form.descriptionLabel')}
            />
            {/* Admin Phone */}
            <div style={{ marginBottom: 15 }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
                {t('teams.form.adminPhoneLabel')} *
              </label>
              <Field name="adminPhone">
                {({ field, meta }: FieldProps) => (
                  <>
                    <input
                      {...field}
                      type="tel"
                      placeholder={t('teams.form.adminPhonePlaceholder')}
                      className="ui input"
                      style={{ width: '100%', padding: '9px 14px', borderRadius: 4, border: '1px solid rgba(34,36,38,.15)' }}
                      onKeyDown={(e) => {
                        // Dozvoli: cifre, +, -, razmak, (, ), Backspace, Delete, Tab, strelice
                        const allowed = /[0-9\+\-\s\(\)]/;
                        if (!allowed.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                    {meta.touched && meta.error && (
                      <div style={{ color: '#9f3a38', marginTop: 4, fontSize: 13 }}>{meta.error}</div>
                    )}
                  </>
                )}
              </Field>
            </div>
            {/* Location */}
            <Header content={t('teams.form.locationLabel')} sub color="teal" />
            <GameLocationPicker
              onLocationSelect={(lat, lng) => {
                setFieldValue('latitude', lat);
                setFieldValue('longitude', lng);
              }}
              initialLat={values.latitude || undefined}
              initialLng={values.longitude || undefined}
            />

            {/* Logo upload — edit mode only */}
            {team && (
              <div style={{ marginBottom: 20 }}>
                <Header content={t('teams.form.logoLabel')} sub color="teal" />
                {team.logoUrl && (
                  <Image src={team.logoUrl} size="small" rounded style={{ marginBottom: 10 }} />
                )}
                <Button
                  type="button"
                  size="small"
                  basic
                  onClick={() => setShowLogoUpload(v => !v)}
                >
                  <Icon name="upload" />
                  {t('teams.form.uploadLogo')}
                </Button>

                {showLogoUpload && (
                  <div style={{ marginTop: 12 }}>
                    <PhotoUploadWidget
                      loading={teamStore.submitting}
                      uploadPhoto={async (file) => {
                        const url = await teamStore.uploadLogo(team.id, file);
                        if (url) setShowLogoUpload(false);
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <Button
                positive
                type="submit"
                content={team ? t('teams.form.save') : t('teams.form.create')}
                disabled={!isValid || isSubmitting || teamStore.submitting}
                loading={teamStore.submitting}
              />
              <Button
                type="button"
                content={t('teams.form.cancel')}
                onClick={() => navigate(-1)}
              />
            </div>
          </Form>
        )}
      </Formik>
    </Segment>
  );
});